import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, FileText, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { hasPermission } from "../lib/utils/permissions";
import { useAuthStore } from "../context/AuthContext";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import AdminListToolbar from "../components/common/AdminListToolbar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";


const Results = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canCreate = hasPermission(user, "results", "create");
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [pdfFilter, setPdfFilter] = useState("all");

  const years = useMemo(() => [...new Set(items.map((item) => String(item.year || "")).filter(Boolean))]
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true })), [items]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const hasPdf = Boolean(item.pdf_url);
      const matchesYear = yearFilter === "all" || String(item.year) === yearFilter;
      const matchesPdf = pdfFilter === "all"
        || (pdfFilter === "with-pdf" && hasPdf)
        || (pdfFilter === "without-pdf" && !hasPdf);
      const matchesSearch = !query || [item.title, item.year]
        .some((value) => String(value || "").toLowerCase().includes(query));
      return matchesYear && matchesPdf && matchesSearch;
    });
  }, [items, pdfFilter, search, yearFilter]);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/results");
      setItems(response.data?.data?.results || []);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load results");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/results/${item.id}`);
      showSuccessToast("Result deleted successfully");
      fetchItems();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete result");
    }
  };

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">College Results</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Manage result title, PDF, and year.</p>
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="rounded-lg" onClick={fetchItems}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canCreate && (

            <Button type="button" className="rounded-lg" onClick={() => navigate("/results/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Add Result
          </Button>

          )}
        </div>
      </div>

      <AdminListToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search result title or year..."
        searchLabel="Search college results"
        filteredCount={filteredItems.length}
        totalCount={items.length}
        itemLabel="results"
        hasActiveFilters={Boolean(search) || yearFilter !== "all" || pdfFilter !== "all"}
        onReset={() => {
          setSearch("");
          setYearFilter("all");
          setPdfFilter("all");
        }}
      >
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="min-w-40" aria-label="Filter results by year">
            <SelectValue placeholder="All years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All years</SelectItem>
            {years.map((year) => <SelectItem key={year} value={year}>{year}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={pdfFilter} onValueChange={setPdfFilter}>
          <SelectTrigger className="min-w-40" aria-label="Filter results by PDF">
            <SelectValue placeholder="PDF status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PDF statuses</SelectItem>
            <SelectItem value="with-pdf">With PDF</SelectItem>
            <SelectItem value="without-pdf">Without PDF</SelectItem>
          </SelectContent>
        </Select>
      </AdminListToolbar>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">Loading results...</div>
        ) : filteredItems.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">
            {items.length === 0 ? "No results found." : "No results match your filters."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Title</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Year</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">PDF</th>
                  <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-800">{item.title}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">{item.year}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      <a href={item.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
                        <FileText className="mr-2 h-3.5 w-3.5" />
                        Open PDF
                        <ExternalLink className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(`/results/${item.id}/edit`)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <DeleteConfirmationButton onConfirm={() => handleDelete(item)}>
<Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
</DeleteConfirmationButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Results;
